import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../Common/Configurations/APIConfig';

const BusinessProfile = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [salonId, setSalonId] = useState('');
    const [profileData, setProfileData] = useState({
        salonId: '',
        name: '',
        slug: '',
        phone: '',
        email: '',
        address: '',
        timeZone: '',
        status: true,
        createdAt: ''
    });
    const [formData, setFormData] = useState({ ...profileData });

    // Debug effect - remove in production
    useEffect(() => {
        console.log('Current profileData:', profileData);
        console.log('Current formData:', formData);
        console.log('Current salonId:', salonId);
        console.log('Is editing:', isEditing);
    }, [profileData, formData, salonId, isEditing]);

    useEffect(() => {
        // Get salonId from localStorage
        const storedSalonId = localStorage.getItem('salonId');
        console.log('Retrieved salonId from localStorage:', storedSalonId);
        
        if (storedSalonId) {
            setSalonId(storedSalonId);
            fetchBusinessProfile(storedSalonId);
        } else {
            console.error('No salonId found in localStorage');
            alert('No salon found. Please login again.');
        }
    }, []);

    const fetchBusinessProfile = async (salonId) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            
            console.log('Fetching salon with ID:', salonId);
            const apiUrl = `${config.APIACTIVEURL}${config.GETSALONSBYID}/${salonId}`;
            console.log('API URL:', apiUrl);
            
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            console.log('Full API Response:', response);
            
            // Handle different response structures
            let salonData = null;
            
            if (response.data) {
                if (response.data.data && typeof response.data.data === 'object') {
                    // Structure: { success: true, data: {...} }
                    salonData = response.data.data;
                } else if (response.data && typeof response.data === 'object' && response.data.salonId) {
                    // Structure: { salonId: "...", name: "...", ... }
                    salonData = response.data;
                } else if (response.data.success && response.data.data) {
                    // Structure: { success: true, data: {...} }
                    salonData = response.data.data;
                }
            }
            
            if (salonData) {
                console.log('Extracted salon data:', salonData);
                
                // Format the data with fallback values
                const formattedData = {
                    salonId: salonData.salonId || salonId || '',
                    name: salonData.name || 'No Name Provided',
                    slug: salonData.slug || '',
                    phone: salonData.phone || salonData.phoneNumber || '',
                    email: salonData.email || '',
                    address: salonData.address || '',
                    timeZone: salonData.timeZone || 'Asia/Calcutta',
                    status: salonData.status !== undefined ? Boolean(salonData.status) : true,
                    createdAt: salonData.createdAt || new Date().toISOString()
                };
                
                console.log('Formatted data for state:', formattedData);
                
                setProfileData(formattedData);
                setFormData(formattedData);
            } else {
                console.error('Could not extract salon data from response:', response.data);
                alert('Failed to load business profile - invalid response format');
            }
        } catch (error) {
            console.error('Error fetching business profile:', error);
            
            if (error.response) {
                console.error('Error response status:', error.response.status);
                console.error('Error response data:', error.response.data);
                alert(`Error ${error.response.status}: ${error.response.data?.message || 'Failed to load business profile'}`);
            } else if (error.request) {
                console.error('No response received:', error.request);
                alert('No response from server. Please check your connection.');
            } else {
                console.error('Error setting up request:', error.message);
                alert(`Error: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!salonId) {
            alert('No salon selected. Please login again.');
            return;
        }
        
        try {
            setSaving(true);
            const token = localStorage.getItem('token');

            // Prepare update data
            const updateData = {
                salonId: salonId,
                name: formData.name,
                slug: formData.slug,
                phone: formData.phone,
                email: formData.email,
                address: formData.address,
                status: formData.status
            };

            console.log('Sending update data:', updateData);
            
            const response = await axios.put(
                `${config.APIACTIVEURL}${config.UPDATESALON}`,
                updateData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log('Update response:', response);

            if (response.data.success) {
                // Handle response data (might be in response.data.data or response.data)
                const updatedData = response.data.data || response.data;
                setProfileData(updatedData);
                setFormData(updatedData);
                setIsEditing(false);
                alert('Business profile updated successfully!');
            } else {
                alert(response.data.message || 'Failed to update business profile.');
            }
        } catch (error) {
            console.error('Error updating business profile:', error);
            if (error.response) {
                alert(error.response.data?.message || 'An error occurred while updating business profile.');
            } else {
                alert('Network error. Please check your connection.');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData({ ...profileData });
        setIsEditing(false);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return 'Invalid Date';
        }
    };



    if (!salonId) {
        return (
            <div className="container-fluid py-4" style={{ paddingTop: '100px' }}>
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <div className="alert alert-warning">
                                    <i className="ri-store-line me-2" style={{ fontSize: '24px' }}></i>
                                    <h4 className="alert-heading">No Salon Selected</h4>
                                    <p>Please login again or select a salon to continue.</p>
                                    <button 
                                        className="btn btn-primary mt-2"
                                        onClick={() => window.location.reload()}
                                    >
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="container-fluid py-4" style={{ paddingTop: '100px' }}>
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading business profile...</p>
                               
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4" style={{ paddingTop: '100px' }}>
            {/* Debug Panel - Remove this in production */}
            <div style={{
                position: 'fixed',
                top: '10px',
                right: '10px',
                background: '#f8f9fa',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                zIndex: 9999,
                maxWidth: '300px',
                maxHeight: '200px',
                overflow: 'auto',
                fontSize: '12px',
                display: 'none' // Set to 'block' to see debug info
            }}>
                <h6>Debug Info</h6>
                <p><strong>Salon ID:</strong> {salonId}</p>
                <p><strong>Profile Keys:</strong> {Object.keys(profileData).join(', ')}</p>
                <button 
                    onClick={() => console.log('Profile Data:', profileData, 'Form Data:', formData)}
                    className="btn btn-sm btn-outline-secondary"
                >
                    Log Data
                </button>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-0 py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h3 className="mb-0 fw-bold text-dark">Business Profile</h3>
                                    
                                </div>
                                <div>
                                    {!isEditing ? (
                                        <button 
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={() => setIsEditing(true)}
                                            disabled={loading}
                                        >
                                            <i className="ri-edit-line me-1"></i> Edit Business Profile
                                        </button>
                                    ) : (
                                        <div className="d-flex gap-2">
                                            <button 
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={handleCancel}
                                                disabled={saving}
                                            >
                                                Cancel
                                            </button>
                                            <button 
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={handleSubmit}
                                                disabled={saving}
                                            >
                                                {saving ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-1"></i> Save Changes
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    {/* Left Column - Basic Info */}
                                    <div className="col-lg-4">
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-body text-center">
                                                <div className="position-relative d-inline-block mb-3">
                                                    <div className="rounded-circle overflow-hidden" 
                                                         style={{ 
                                                             width: '150px', 
                                                             height: '150px', 
                                                             margin: '0 auto'
                                                         }}>
                                                        <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-primary bg-opacity-10">
                                                            <i className="ri-store-line text-primary" style={{ fontSize: '60px' }}></i>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={formData.name || ''}
                                                        onChange={handleInputChange}
                                                        className="form-control text-center fw-bold mb-2"
                                                        required
                                                        disabled={saving}
                                                        placeholder="Business Name"
                                                    />
                                                ) : (
                                                    <h4 className="mb-2">{profileData.name || 'No Business Name'}</h4>
                                                )}
                                                
                                               
                                            </div>
                                        </div>

                                        {/* Status Card */}
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-white">
                                                <h5 className="mb-0 fw-bold">Business Status</h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="mb-3">
                                                    <label className="form-label text-muted small">Status</label>
                                                   
                                                        
                                                 
                                                        <p className="mb-0">
                                                            {profileData.status ? (
                                                                <span className="badge bg-success">Active</span>
                                                            ) : (
                                                                <span className="badge bg-danger">Inactive</span>
                                                            )}
                                                        </p>
                                                   
                                                </div>
                                               
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column - Business Details */}
                                    <div className="col-lg-8">
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-header bg-white">
                                                <h5 className="mb-0 fw-bold">Business Information</h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label fw-medium">Business Name *</label>
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                name="name"
                                                                value={formData.name || ''}
                                                                onChange={handleInputChange}
                                                                className="form-control"
                                                                required
                                                                disabled={saving}
                                                                placeholder="Enter business name"
                                                            />
                                                        ) : (
                                                            <p className="mb-0">{profileData.name || 'Not provided'}</p>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label fw-medium">Email</label>
                                                        {isEditing ? (
                                                            <input
                                                                type="email"
                                                                name="email"
                                                                value={formData.email || ''}
                                                                onChange={handleInputChange}
                                                                className="form-control"
                                                                disabled={saving}
                                                                placeholder="business@example.com"
                                                            />
                                                        ) : (
                                                            <p className="mb-0">{profileData.email || 'Not provided'}</p>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label fw-medium">Phone Number</label>
                                                        {isEditing ? (
                                                            <input
                                                                type="tel"
                                                                name="phone"
                                                                value={formData.phone || ''}
                                                                onChange={handleInputChange}
                                                                className="form-control"
                                                                disabled={saving}
                                                                placeholder="(123) 456-7890"
                                                            />
                                                        ) : (
                                                            <p className="mb-0">{profileData.phone || 'Not provided'}</p>
                                                        )}
                                                    </div>
                                                   
                                                    <div className="col-12 mb-3">
                                                        <label className="form-label fw-medium">Business Address</label>
                                                        {isEditing ? (
                                                            <textarea
                                                                name="address"
                                                                value={formData.address || ''}
                                                                onChange={handleInputChange}
                                                                className="form-control"
                                                                rows="3"
                                                                disabled={saving}
                                                                placeholder="Enter full business address"
                                                            />
                                                        ) : (
                                                            <p className="mb-0">{profileData.address || 'Not provided'}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Additional Information */}
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-white">
                                                <h5 className="mb-0 fw-bold">Additional Information</h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                  
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label text-muted small">Created Date</label>
                                                        <p className="mb-0 fw-medium">{formatDate(profileData.createdAt)}</p>
                                                    </div>
                                                   
                                                   
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BusinessProfile;